// Schedule-specific formatting + the route id parser. No DOM, no state.
import { escapeHtml } from "./format.js";
import { formatPence } from "./format.js";
import { formatInvoicePreviewDate } from "./invoice-format.js";

export function getScheduleIdFromPath(path) {
  const match = String(path || "").match(/^\/schedules\/([^/]+)$/);
  if (!match) return "";
  try { return decodeURIComponent(match[1]); } catch { return match[1]; }
}

export function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function formatFrequencyLabel(frequency) {
  switch (frequency) {
    case "weekly": return "Weekly";
    case "biweekly": return "Every 2 weeks";
    case "monthly": return "Monthly";
    case "quarterly": return "Quarterly";
    case "annually": return "Annually";
    default: return frequency || "Monthly";
  }
}

export function formatScheduleStatus(status) {
  switch (status) {
    case "draft": return "Draft";
    case "active": return "Active";
    case "paused": return "Paused";
    case "ended": return "Ended";
    case "failed": return "Failed";
    default: return status || "";
  }
}

export function scheduleStatusTagClass(status) {
  // Reuse the shared status-tag colour classes (Figma Tag 13:35, defined in
  // base.css as `.app-invoice-status-*`) — there is no separate schedule
  // palette. The previous `ar-td-status-tag-*` names were never defined, so every
  // schedule tag fell back to the bare muted-grey base (active/paused/ended all
  // looked identical). active → green, paused/failed → orange, ended/draft → grey.
  switch (status) {
    case "active": return "app-invoice-status-paid";
    case "paused": return "app-invoice-status-overdue";
    case "failed": return "app-invoice-status-overdue";
    case "ended": return "app-invoice-status-draft";
    case "draft": return "app-invoice-status-draft";
    default: return "";
  }
}

export function buildScheduleSentence(schedule) {
  const contact = schedule.contactName || "…";
  // The schedule's own total, in the pence the API sends (the live create
  // form passes the same field off its typed figures).
  const totalPence = Number(schedule.totalPence) || 0;
  const amount = totalPence > 0 ? formatPence(totalPence, schedule.currency) : "…";

  let frequencyText = "…";
  if (schedule.startDate) {
    // startDate may arrive as a bare "YYYY-MM-DD" (from the live create form) or
    // as a full ISO timestamp ("2026-06-01T00:00:00.000Z") — the API serialises
    // the Postgres DATE column as a JS Date, which JSON renders as a timestamp.
    // Take the date portion before appending the time so the concat can never
    // yield an invalid date (which surfaced as "the NaNth of every month").
    const dateOnly = String(schedule.startDate).slice(0, 10);
    const d = new Date(dateOnly + "T00:00:00Z");
    if (!Number.isNaN(d.getTime())) {
      const dayNum = d.getUTCDate();
      const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][d.getUTCDay()];
      switch (schedule.frequency) {
        case "weekly":    frequencyText = `every ${dayName}`; break;
        case "biweekly":  frequencyText = `every other ${dayName}`; break;
        case "monthly":   frequencyText = `on the ${ordinal(dayNum)} of every month`; break;
        case "quarterly": frequencyText = `on the ${ordinal(dayNum)} of every 3 months`; break;
        case "annually": {
          const month = d.toLocaleString("en-GB", { month: "long", timeZone: "UTC" });
          frequencyText = `every year on ${month} ${ordinal(dayNum)}`;
          break;
        }
      }
    }
  }

  const startText = schedule.startDate ? formatInvoicePreviewDate(schedule.startDate) : "…";
  const endText = schedule.maxOccurrences
    ? `, for ${schedule.maxOccurrences} invoice${Number(schedule.maxOccurrences) === 1 ? "" : "s"}`
    : schedule.endDate
      ? `, until ${formatInvoicePreviewDate(schedule.endDate)}`
      : ", until cancelled";
  const dueText = schedule.dueDaysAfter === 0
    ? "Due on receipt."
    : schedule.dueDaysAfter > 0
      ? `Due in ${schedule.dueDaysAfter} days.`
      : "";
  const deliveryText = schedule.deliveryMode === "auto_send" ? "Sent automatically." : "Saved as a draft.";

  return `Create an invoice for ${escapeHtml(contact)} for ${escapeHtml(amount)} ${escapeHtml(frequencyText)}, starting ${escapeHtml(startText)}${escapeHtml(endText)}. ${escapeHtml(dueText)} ${escapeHtml(deliveryText)}`.trim();
}

export function buildLiveSentence({ contactName, totalPence, frequency, startDate, endDate, maxOccurrences, dueDaysAfter, deliveryMode, currency }) {
  return buildScheduleSentence({ contactName, totalPence, frequency, startDate, endDate, maxOccurrences, dueDaysAfter, deliveryMode, currency });
}
